import React from 'react';
import humanChatService from '../../services/humanChatService';
import '../../styles/group-chat-brutal.css';

/**
 * List of conversations (group chat + bilateral chats with guides)
 * Brutal design style
 */
const ConversationList = ({
  conversations = [],
  currentUserId,
  onSelectConversation,
  onNewChat
}) => {
  // Format timestamp for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'NOW';
    if (diffMins < 60) return `${diffMins}M`;
    if (diffHours < 24) return `${diffHours}H`;
    if (diffDays < 7) return `${diffDays}D`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get conversation display info
  const getConversationInfo = (conversation) => {
    const isGroup = conversation.type === 'group';

    if (isGroup) {
      return {
        name: conversation.name || 'Group Chat',
        icon: '👥',
        isGroup: true
      };
    }

    const other = humanChatService.getOtherParticipant(conversation, currentUserId);
    return {
      name: other?.nickname || other?.username || 'Unknown',
      icon: other?.avatar ? null : '👤',
      avatar: other?.avatar,
      isGroup: false
    };
  };

  // Separate group chat from bilateral chats
  const groupChat = conversations.find(c => c.type === 'group');
  const bilateralChats = conversations.filter(c => c.type === 'bilateral');

  // Sort bilateral chats by last message time
  const sortedBilateralChats = [...bilateralChats].sort((a, b) => {
    const timeA = new Date(a.lastMessageAt || 0);
    const timeB = new Date(b.lastMessageAt || 0);
    return timeB - timeA;
  });

  if (conversations.length === 0) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <span className="conversation-list-title">Conversations</span>
          <button className="new-chat-btn" onClick={onNewChat}>
            + New Chat
          </button>
        </div>

        <div className="conversation-empty">
          <div className="conversation-empty-icon">💬</div>
          <div className="conversation-empty-title">No conversations yet</div>
          <div className="conversation-empty-text">
            Start a chat with a guide to get help with your investments!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <span className="conversation-list-title">Conversations</span>
        <button className="new-chat-btn" onClick={onNewChat}>
          + New Chat
        </button>
      </div>

      {/* Group Chat (always at top if exists) */}
      {groupChat && (
        <>
          <div className="conversation-list-title" style={{ marginTop: 8, marginBottom: 8, fontSize: '0.75rem' }}>
            GROUP
          </div>
          <ConversationItem
            conversation={groupChat}
            info={getConversationInfo(groupChat)}
            formatTime={formatTime}
            onClick={() => onSelectConversation(groupChat)}
          />
        </>
      )}

      {/* Bilateral Chats */}
      {sortedBilateralChats.length > 0 && (
        <>
          <div className="conversation-list-title" style={{ marginTop: 16, marginBottom: 8, fontSize: '0.75rem' }}>
            GUIDE CHATS
          </div>
          {sortedBilateralChats.map(conversation => (
            <ConversationItem
              key={conversation._id}
              conversation={conversation}
              info={getConversationInfo(conversation)}
              formatTime={formatTime}
              onClick={() => onSelectConversation(conversation)}
            />
          ))}
        </>
      )}
    </div>
  );
};

/**
 * Individual conversation item
 */
const ConversationItem = ({ conversation, info, formatTime, onClick }) => {
  const { name, icon, avatar, isGroup } = info;
  const hasUnread = conversation.unreadCount > 0;
  const preview = conversation.lastMessage?.content || 'No messages yet';

  return (
    <div
      className={`conversation-item ${isGroup ? 'group-chat' : ''} ${hasUnread ? 'has-unread' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="conversation-item-header">
        <div className="conversation-item-name">
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              style={{ width: 24, height: 24, borderRadius: 0, border: '2px solid #000' }}
            />
          ) : (
            <span>{icon}</span>
          )}
          <span>{name}</span>
          {isGroup && <span className="role-badge guide">GROUP</span>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasUnread && (
            <span className="conversation-unread-badge">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
          <span className="conversation-item-time">
            {formatTime(conversation.lastMessageAt)}
          </span>
        </div>
      </div>

      <div className="conversation-item-preview">
        {preview.length > 60 ? preview.substring(0, 60) + '...' : preview}
      </div>
    </div>
  );
};

export default ConversationList;
